/** 편성 JSON → 타임라인 (schedule 페이지 · 라이브 엔드 공용) */

export const DAY_MS = 24 * 60 * 60 * 1000;

export function getApiDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
}

/** API date 문자열 → YYYYMMDD (탭·state 키와 동일) */
export function toYmdKey(raw) {
  if (raw == null || raw === '') return '';
  const s = String(raw).trim();
  if (/^\d{8}$/.test(s)) return s;
  const m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (m) {
    return `${m[1]}${String(m[2]).padStart(2, '0')}${String(m[3]).padStart(2, '0')}`;
  }
  const digits = s.replace(/\D/g, '');
  return digits.length >= 8 ? digits.slice(0, 8) : s;
}

function parseClockToParts(tRaw) {
  const parts = String(tRaw ?? '0').trim().split(':');
  let h = parseInt(parts[0], 10);
  let m = parseInt(parts[1], 10);
  if (!Number.isFinite(h)) h = 0;
  if (!Number.isFinite(m)) m = 0;
  let extraDays = 0;
  while (h >= 24) {
    h -= 24;
    extraDays += 1;
  }
  return { extraDays, h, m };
}

/** 편성표 UI 표시용 — 24:10 → 00:10 (API·타임스탬프 계산은 그대로) */
export function formatScheduleClockForDisplay(timeRaw) {
  const { h, m } = parseClockToParts(timeRaw);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** 보정된 startTimestamp/endTimestamp → KST HH:mm (표시용) */
export function formatScheduleClockFromTimestamp(ts) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Seoul',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(ts));
  const hour = parts.find((p) => p.type === 'hour')?.value ?? '00';
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
  return `${hour}:${minute}`;
}

/** YYYYMMDD + n일 */
function addDaysToYmdKey(ymd, days) {
  const y = parseInt(ymd.slice(0, 4), 10);
  const mo = parseInt(ymd.slice(4, 6), 10) - 1;
  const d = parseInt(ymd.slice(6, 8), 10);
  const dt = new Date(Date.UTC(y, mo, d + days));
  return getApiDate(dt);
}

/**
 * 편성 JSON 의 HH:mm 은 KST 벽시계.
 * Lambda(UTC) SSR 과 브라우저(KST) 에서 동일 ms 가 되도록 UTC 로 변환한다.
 */
function timestampFromBaseYmd(baseDateKey, extraDays, h, m) {
  const y = parseInt(baseDateKey.slice(0, 4), 10);
  const mo = parseInt(baseDateKey.slice(4, 6), 10);
  const d = parseInt(baseDateKey.slice(6, 8), 10);
  return Date.UTC(y, mo - 1, d + extraDays, h - 9, m, 0, 0);
}

/** 편성 JSON → 절대 시각(ms). 24시대 표기, 자정 넘김·리스트 순서 보정 포함 */
export function buildProgramTimelines(baseDateKey, channelPrograms, options = {}) {
  const list = Array.isArray(channelPrograms) ? channelPrograms : [];
  const nextDayFirstStart = options.nextDayFirstStart
    ? String(options.nextDayFirstStart).trim()
    : '';
  const tomorrowKey = nextDayFirstStart ? addDaysToYmdKey(baseDateKey, 1) : '';
  let prevEndTs = null;
  const out = [];

  for (let i = 0; i < list.length; i++) {
    const program = list[i];
    const stRaw = program.start_time || program.starttime || '00:00';
    const etRaw = program.end_time || program.endtime || '23:59';

    const ss = parseClockToParts(stRaw);
    const ee = parseClockToParts(etRaw);
    let startTs = timestampFromBaseYmd(baseDateKey, ss.extraDays, ss.h, ss.m);
    let endTs = timestampFromBaseYmd(baseDateKey, ee.extraDays, ee.h, ee.m);
    if (endTs <= startTs) {
      endTs += DAY_MS;
    }

    // 다음 행(당일) 또는 익일 첫 편성 시작으로 종료 시각 보정
    const nextRow = list[i + 1];
    const stNextRaw = nextRow
      ? nextRow.start_time || nextRow.starttime || '00:00'
      : i === list.length - 1 && nextDayFirstStart
        ? nextDayFirstStart
        : '';
    if (stNextRaw) {
      const ssNext = parseClockToParts(stNextRaw);
      const nextBase = nextRow ? baseDateKey : tomorrowKey;
      const nextStartTs = timestampFromBaseYmd(
        nextBase,
        ssNext.extraDays,
        ssNext.h,
        ssNext.m
      );
      if (nextStartTs > startTs && (nextStartTs < endTs || !nextRow)) {
        endTs = nextStartTs;
      }
    }

    if (prevEndTs != null && startTs < prevEndTs) {
      while (startTs < prevEndTs) {
        startTs += DAY_MS;
        endTs += DAY_MS;
      }
    }

    prevEndTs = endTs;

    const startDate = getApiDate(new Date(startTs));

    out.push({
      title: program.title,
      startTime: formatScheduleClockFromTimestamp(startTs),
      endTime:
        endTs > startTs + 60 * 1000 ? formatScheduleClockFromTimestamp(endTs) : '',
      startDate,
      startTimestamp: startTs,
      endTimestamp: endTs,
      programId: String(
        program.program_id ?? program.programId ?? program.programid ?? program.content_id ?? ''
      ).trim(),
      vodUrl: String(program.vod_url ?? program.vodUrl ?? program.link_vod ?? '').trim(),
      circleImage: String(
        program.circle_image ?? program.circleImage ?? program.img ?? ''
      ).trim(),
    });
  }

  return out;
}

/** now가 속한 구간이 여러 개일 때 시작이 가장 늦은 슬롯을 LIVE로 */
export function findLiveProgramIndex(programs, nowTs) {
  if (!programs?.length) return -1;
  let bestIdx = -1;
  let bestStart = -Infinity;
  programs.forEach((p, i) => {
    if (nowTs < p.startTimestamp || nowTs >= p.endTimestamp) return;
    if (p.startTimestamp > bestStart || (p.startTimestamp === bestStart && i > bestIdx)) {
      bestStart = p.startTimestamp;
      bestIdx = i;
    }
  });
  return bestIdx;
}

/**
 * now 가 어느 편성 구간에도 속하지 않을 때(공백·데이터 경계 등) 스크롤 기준 인덱스.
 * 아직 종료 전인 첫 행, 모두 지난 날이면 마지막 행.
 */
export function findTimeAnchorProgramIndex(programs, nowTs) {
  if (!programs?.length) return -1;
  const inSlot = findLiveProgramIndex(programs, nowTs);
  if (inSlot >= 0) return inSlot;
  for (let i = 0; i < programs.length; i++) {
    if (nowTs < programs[i].endTimestamp) return i;
  }
  return programs.length - 1;
}

/** 라이브 엔드: 현재 시각 편성(앵커)부터 이후만 — 종료된 이전 편성 제거 */
export function sliceProgramsFromCurrentSlot(programs, nowTs) {
  if (!programs?.length) return [];
  const anchor = findTimeAnchorProgramIndex(programs, nowTs);
  if (anchor < 0) return [];
  return programs.slice(anchor);
}

export function processScheduleResponse(data) {
  const scheduleList = data?.schedule;
  if (!scheduleList) return [];

  const result = [];

  scheduleList.forEach((daySchedule) => {
    const baseDateKey = toYmdKey(daySchedule.date);
    if (!baseDateKey || baseDateKey.length !== 8) return;

    daySchedule.channels.forEach((channel) => {
      const seenKeys = new Set();
      const programs = buildProgramTimelines(baseDateKey, channel.programs).filter(
        (program, idx) => {
          if (idx === 0) return true;
          const key = program.title + program.startTime + channel.channel_code + program.startDate;
          if (seenKeys.has(key)) return false;
          seenKeys.add(key);
          return true;
        }
      );

      result.push({
        date: baseDateKey,
        channelId: String(channel.channel_code),
        channelName: channel.channel,
        programs,
      });
    });
  });

  return result;
}

export function extractProgramArrayFromChannelJson(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.list)) return data.list;
  if (Array.isArray(data.programs)) return data.programs;
  if (Array.isArray(data.schedule)) {
    const out = [];
    data.schedule.forEach((day) => {
      (day.channels || []).forEach((ch) => {
        (ch.programs || []).forEach((p) => out.push(p));
      });
    });
    return out;
  }
  return [];
}

export function normalizeProgramRowsForSchedule(rawArray) {
  const arr = Array.isArray(rawArray) ? rawArray : [];
  return arr.map((p, i) => {
    const title = p.title ?? p.program_title ?? p.programTitle ?? '';
    const st = String(p.start_time ?? p.starttime ?? '00:00').trim() || '00:00';
    let et = p.end_time ?? p.endtime;
    if (et == null || String(et).trim() === '') {
      const next = arr[i + 1];
      const nextSt = next && String(next.start_time ?? next.starttime ?? '').trim();
      et = nextSt || '23:59';
    }
    const programId = String(
      p.programId ?? p.program_id ?? p.programid ?? p.content_id ?? ''
    ).trim();
    const vodUrl = String(p.vod_url ?? p.vodUrl ?? p.link_vod ?? '').trim();
    const circleImage = String(p.circle_image ?? p.circleImage ?? p.img ?? '').trim();
    return {
      title,
      start_time: st,
      end_time: String(et).trim() || '23:59',
      program_id: programId,
      vod_url: vodUrl,
      circle_image: circleImage,
    };
  });
}
